/**
 * useSubscription Hook - Manage subscription state for athlete-web
 *
 * Provides subscription info and feature access checks across the app.
 * Features can be gated based on subscription tier.
 * Pricing is fetched from platform config (admin-configurable).
 */

import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  getMySubscription,
  createCheckoutSession,
  createPortalSession,
  getPricing,
  type UserSubscription,
  type SubscriptionTier,
  type PricingResponse,
  type TierPricing,
} from '@tss/shared';

// Features that require specific tiers
export type PremiumFeature =
  | 'ai_highlights' // AI auto-detect highlights
  | 'ai_analysis' // View AI video analysis
  | 'publish_reels' // Publish and share highlight reels
  | 'ai_chat' // AI chat assistant
  | 'advanced_stats'; // Advanced statistics

// Map features to minimum required tier
const FEATURE_TIERS: Record<PremiumFeature, SubscriptionTier[]> = {
  ai_highlights: ['ATHLETE_BASIC', 'ATHLETE_PRO', 'COACH_BASIC', 'COACH_PRO', 'ENTERPRISE'],
  ai_analysis: ['ATHLETE_BASIC', 'ATHLETE_PRO', 'COACH_BASIC', 'COACH_PRO', 'ENTERPRISE'],
  publish_reels: ['ATHLETE_BASIC', 'ATHLETE_PRO', 'COACH_BASIC', 'COACH_PRO', 'ENTERPRISE'],
  ai_chat: ['ATHLETE_PRO', 'COACH_BASIC', 'COACH_PRO', 'ENTERPRISE'],
  advanced_stats: ['ATHLETE_PRO', 'COACH_PRO', 'ENTERPRISE'],
};

// Feature display info for upgrade prompts
export const FEATURE_INFO: Record<
  PremiumFeature,
  {
    name: string;
    description: string;
    minimumTier: SubscriptionTier;
  }
> = {
  ai_highlights: {
    name: 'AI Highlight Detection',
    description: 'Automatically find your best moments using AI',
    minimumTier: 'ATHLETE_BASIC',
  },
  ai_analysis: {
    name: 'AI Video Analysis',
    description: 'Get AI-powered feedback on your technique',
    minimumTier: 'ATHLETE_BASIC',
  },
  publish_reels: {
    name: 'Publish & Share Reels',
    description: 'Share your highlight reels with anyone',
    minimumTier: 'ATHLETE_BASIC',
  },
  ai_chat: {
    name: 'AI Training Assistant',
    description: 'Chat with AI for personalized training advice',
    minimumTier: 'ATHLETE_PRO',
  },
  advanced_stats: {
    name: 'Advanced Statistics',
    description: 'Deep analytics and performance trends',
    minimumTier: 'ATHLETE_PRO',
  },
};

// Default pricing (fallback if API fails)
const DEFAULT_PRICING: Record<string, TierPricing> = {
  ATHLETE_BASIC: {
    name: 'Athlete Basic',
    description: 'Great for getting started',
    price_cents: 999,
    price_display: '$9.99',
    interval: 'month',
    features: ['AI highlight detection', 'AI video analysis', 'Publish & share reels'],
    ai_credits_monthly: 50,
  },
  ATHLETE_PRO: {
    name: 'Athlete Pro',
    description: 'For serious athletes',
    price_cents: 1999,
    price_display: '$19.99',
    interval: 'month',
    features: [
      'Everything in Basic',
      'AI training assistant',
      'Advanced statistics',
      'Priority support',
    ],
    ai_credits_monthly: 200,
  },
};

export interface SubscriptionState {
  subscription: UserSubscription | null;
  pricing: PricingResponse | null;
  loading: boolean;
  error: string | null;
  // Computed helpers
  tier: SubscriptionTier;
  isActive: boolean;
  isPro: boolean;
  isCoachTier: boolean; // Coaches don't pay AI credits
  hasAICredits: boolean;
  aiCreditsRemaining: number;
  // Pricing helpers
  getTierPricing: (tier: SubscriptionTier) => TierPricing | null;
  // Feature checks
  canAccess: (feature: PremiumFeature) => boolean;
  // Actions
  refresh: () => Promise<void>;
  openCheckout: (tier: SubscriptionTier) => Promise<void>;
  openBillingPortal: () => Promise<void>;
}

// Create context
const SubscriptionContext = createContext<SubscriptionState | null>(null);

export function useSubscription(): SubscriptionState {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

// Hook for creating subscription state (used by provider)
export function useSubscriptionState(): SubscriptionState {
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSubscription = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sub = await getMySubscription();
      setSubscription(sub);
    } catch (err) {
      // 404 means no subscription - treat as FREE tier
      if (err instanceof Error && err.message.includes('404')) {
        setSubscription(null);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load subscription');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPricing = useCallback(async () => {
    try {
      const pricingData = await getPricing();
      setPricing(pricingData);
    } catch (err) {
      console.error('Failed to load pricing:', err);
      // Use defaults if pricing fails to load
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadSubscription();
    loadPricing();
  }, [loadSubscription, loadPricing]);

  // Computed values
  const tier: SubscriptionTier = subscription?.tier || 'FREE';
  const isActive = subscription?.status === 'ACTIVE' || subscription?.status === 'TRIAL';
  const isPro = tier === 'ATHLETE_PRO' || tier === 'COACH_PRO' || tier === 'ENTERPRISE';
  // Coaches/owners have unlimited AI access (don't pay credits)
  const isCoachTier = tier === 'COACH_BASIC' || tier === 'COACH_PRO' || tier === 'ENTERPRISE';
  const hasAICredits = (subscription?.ai_credits_balance ?? 0) > 0;
  const aiCreditsRemaining = subscription?.ai_credits_balance ?? 0;

  // Get pricing for a tier (from API or fallback)
  const getTierPricing = useCallback(
    (targetTier: SubscriptionTier): TierPricing | null => {
      if (pricing?.tiers) {
        const tierKey = targetTier as keyof typeof pricing.tiers;
        if (tierKey in pricing.tiers) {
          return pricing.tiers[tierKey];
        }
      }
      // Fallback to defaults
      return DEFAULT_PRICING[targetTier] || null;
    },
    [pricing]
  );

  // Check if user can access a feature
  const canAccess = useCallback(
    (feature: PremiumFeature): boolean => {
      if (!isActive && tier !== 'FREE') return false;
      const allowedTiers = FEATURE_TIERS[feature];
      return allowedTiers.includes(tier);
    },
    [tier, isActive]
  );

  // Open Stripe checkout
  const openCheckout = useCallback(async (targetTier: SubscriptionTier) => {
    try {
      const { checkout_url } = await createCheckoutSession({
        tier: targetTier,
        success_url: `${window.location.origin}/settings?billing=success`,
        cancel_url: `${window.location.origin}/settings?billing=cancelled`,
      });
      window.location.href = checkout_url;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to create checkout session');
    }
  }, []);

  // Open Stripe billing portal
  const openBillingPortal = useCallback(async () => {
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to open billing portal');
    }
  }, []);

  return {
    subscription,
    pricing,
    loading,
    error,
    tier,
    isActive,
    isPro,
    isCoachTier,
    hasAICredits,
    aiCreditsRemaining,
    getTierPricing,
    canAccess,
    refresh: loadSubscription,
    openCheckout,
    openBillingPortal,
  };
}

// Export context for provider
export { SubscriptionContext };
